import { bundleTasks, type GeneratedTask } from '../lib/flux';
import CodeBlock from './CodeBlock';

interface Props {
  tasks: GeneratedTask[];
  onNotify: (message: string) => void;
}

function download(filename: string, contents: string) {
  const blob = new Blob([contents], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

async function copy(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard access is denied in some embedding contexts; fall back to a
    // selection-based copy so the button still does something useful.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  }
}

export default function FluxPanel({ tasks, onNotify }: Props) {
  return (
    <section aria-labelledby="flux-heading" className="stack" data-testid="flux-panel">
      <div className="panel">
        <header>
          <h2 id="flux-heading">Generated Flux tasks</h2>
          <span className="tier-actions">
            <button
              type="button"
              data-testid="download-all"
              disabled={tasks.length === 0}
              onClick={() => download('downsample-tasks.flux', bundleTasks(tasks))}
            >
              Download all
            </button>
          </span>
        </header>
        <div className="panel-body">
          <p className="hint" style={{ margin: 0 }}>
            One task per tier, in cascade order. Each script truncates its query range to whole
            aggregation windows, so a run never writes a window that is still filling and re-running
            a task is idempotent. Create them with{' '}
            <code className="inline-code">influx task create -f &lt;file&gt;</code> or paste into
            the Data Explorer.
          </p>
        </div>
      </div>

      {tasks.map((task) => (
        <CodeBlock
          key={task.name}
          title={task.filename}
          code={task.script}
          description={`Flux task writing into the ${task.bucket} bucket`}
          actions={
            <>
              <button
                type="button"
                data-testid={`copy-${task.bucket}`}
                onClick={async () => {
                  const ok = await copy(task.script);
                  onNotify(
                    ok ? `Copied ${task.filename}` : 'Copy failed — select the text instead',
                  );
                }}
              >
                Copy
              </button>
              <button type="button" onClick={() => download(task.filename, task.script)}>
                Download
              </button>
            </>
          }
        />
      ))}
    </section>
  );
}
